import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import { useTheme } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import differenceInCalendarDays from 'date-fns/esm/differenceInCalendarDays'
import { FormikValues } from 'formik'
import { useParams } from 'react-router-dom'

import { i18WithParams as t } from '@shared/locale'
import { dayToJSDate } from 'components-ui'
import { formatDateShort } from 'components-ui/src/shared'
import { ReactNode } from 'react'
import { Hooks, Types } from 'service-api'

interface TPetVaccinesFormProps {
  formik: FormikValues
  lpp?: Types.LocationPetProfileClientDto
}

export const PetVaccinesForm = ({ formik, lpp }: TPetVaccinesFormProps) => {
  const { accountName: locationName = '' } = useParams()
  const vaccines = Hooks.useClientLocationVaccines(locationName, { cacheEnabled: true })

  return (
    <>
      <a id="vaccines" />
      <Typography variant="h6" sx={{ mt: 1, mb: 3 }}>
        {t('profile.pet.vaccinations')}
      </Typography>
      {vaccines.isLoading ? (
        <Skeleton variant="rectangular" height={200} />
      ) : vaccines.isSuccess ? (
        <VaccinesList lpp={lpp} vaccines={vaccines.data.results} specieId={formik.values.specie.species.id} />
      ) : null}
    </>
  )
}

interface TVaccineStatusProps {
  expirationDate?: Date
  required: boolean
}

export const VaccineStatus = ({ expirationDate, required }: TVaccineStatusProps) => {
  const theme = useTheme()

  if (!expirationDate) {
    if (required) {
      return (
        <Stack direction="row" spacing={1}>
          <WarningIcon fontSize="small" sx={{ fill: theme.palette.warning.main }} />
          <Typography variant="body1bold">{t('profile.pet.vaccinationRequired')}</Typography>
        </Stack>
      )
    }
    return null
  }

  const today = new Date()
  const isDueSoon = differenceInCalendarDays(expirationDate, today)
  if (isDueSoon > 0 && isDueSoon < 60) {
    return (
      <Stack direction="row" spacing={1}>
        <WarningIcon fontSize="small" sx={{ fill: theme.palette.warning.main }} />
        <Typography variant="body1bold">{t('profile.pet.vaccinationDueSoon')}</Typography>
      </Stack>
    )
  }

  if (isDueSoon < 0) {
    return (
      <Stack direction="row" spacing={1}>
        <ErrorIcon fontSize="small" sx={{ fill: theme.palette.error.main }} />
        <Typography variant="body1bold">{t('profile.pet.vaccinationPastDue')}</Typography>
      </Stack>
    )
  }

  return null
}

interface TVaccinesListProps {
  lpp?: Types.LocationPetProfileClientDto
  vaccines: Types.LocationVaccineClientDto[]
  specieId: string
}

export const VaccinesList = ({ lpp, vaccines, specieId }: TVaccinesListProps) => {
  const theme = useTheme()
  return vaccines.reduce((acc, vaccine) => {
    if (vaccine.vaccineType?.speciesId !== specieId) return acc
    const requiredByLocation = vaccine.required || false
    const petVaccine = lpp?.vaccinations?.find((v) => v.locationVaccineId === vaccine.id)
    const expirationDate = dayToJSDate(petVaccine?.expirationDate)

    acc.push(
      <Stack
        key={vaccine.id}
        direction="row"
        spacing={2}
        py={1}
        sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
      >
        <Typography flex={1}>{vaccine.displayName || vaccine.vaccineType.displayName}</Typography>
        <Typography>
          {requiredByLocation && !petVaccine?.expirationDate
            ? t('profile.pet.vaccinationNeeded')
            : formatDateShort(expirationDate)}
        </Typography>
        <Typography component="div" sx={{ minWidth: '200px' }}>
          <VaccineStatus expirationDate={expirationDate} required={requiredByLocation} />
        </Typography>
      </Stack>
    )
    return acc
  }, [] as ReactNode[])
}
